import { create } from 'zustand';
import type { List } from '../types/database';

interface ListsState {
  /** All lists for the current user */
  lists: List[];
  /** Currently selected list ID */
  selectedListId: string | null;
  /** Loading state */
  isLoading: boolean;
}

interface ListsActions {
  /** Set all lists */
  setLists: (lists: List[]) => void;
  /** Add a new list */
  addList: (list: List) => void;
  /** Update an existing list */
  updateList: (id: string, updates: Partial<List>) => void;
  /** Remove a list */
  removeList: (id: string) => void;
  /** Select a list */
  selectList: (id: string | null) => void;
  /** Set loading state */
  setIsLoading: (isLoading: boolean) => void;
  /** Clear all state (for sign out) */
  clear: () => void;
}

export type ListsStore = ListsState & ListsActions;

const initialState: ListsState = {
  lists: [],
  selectedListId: null,
  isLoading: false,
};

export const useListsStore = create<ListsStore>((set, get) => ({
  // State
  ...initialState,

  // Actions
  setLists: (lists) => {
    const currentSelectedId = get().selectedListId;
    // If no list is selected, select the default list or the first list
    let selectedListId = currentSelectedId;
    if (!selectedListId || !lists.find((l) => l.id === selectedListId)) {
      const defaultList = lists.find((l) => l.is_default);
      selectedListId = defaultList?.id ?? lists[0]?.id ?? null;
    }
    set({ lists, selectedListId, isLoading: false });
  },

  addList: (list) =>
    set((state) => ({
      lists: [...state.lists, list],
    })),

  updateList: (id, updates) =>
    set((state) => ({
      lists: state.lists.map((list) =>
        list.id === id ? { ...list, ...updates } : list
      ),
    })),

  removeList: (id) =>
    set((state) => {
      const newLists = state.lists.filter((list) => list.id !== id);
      // If the removed list was selected, select the default or first list
      let selectedListId = state.selectedListId;
      if (selectedListId === id) {
        const defaultList = newLists.find((l) => l.is_default);
        selectedListId = defaultList?.id ?? newLists[0]?.id ?? null;
      }
      return { lists: newLists, selectedListId };
    }),

  selectList: (id) => set({ selectedListId: id }),

  setIsLoading: (isLoading) => set({ isLoading }),

  clear: () => set(initialState),
}));

/**
 * Get the currently selected list
 */
export const selectCurrentList = (state: ListsStore): List | null => {
  if (!state.selectedListId) return null;
  return state.lists.find((l) => l.id === state.selectedListId) ?? null;
};

/**
 * Get the default list
 */
export const selectDefaultList = (state: ListsStore): List | null => {
  return state.lists.find((l) => l.is_default) ?? null;
};
